import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { Loader2, Send, Users } from "lucide-react";

interface Profile {
  id: string;
  email: string;
  first_name: string | null;
  plan: string | null;
  is_subscribed: boolean | null;
  email_confirmed: boolean | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSent?: () => void;
}

const TEMPLATES: Record<string, { subject: string; html: string }> = {
  custom: { subject: "", html: "" },
  welcome: {
    subject: "🎉 Bem-vindo ao IA Palpite Guru!",
    html: `<div style="font-family:Arial;padding:20px;max-width:600px"><h2 style="color:#d97706">Bem-vindo!</h2><p>Estamos felizes em ter você conosco. Acesse seus palpites com IA agora mesmo.</p><a href="https://aipalpiteguru.mentepoderosa2030.com" style="background:#d97706;color:#fff;padding:12px 24px;border-radius:6px;text-decoration:none;display:inline-block;margin-top:12px">Acessar Plataforma</a></div>`,
  },
  offer20: {
    subject: "🔥 20% OFF exclusivo para você!",
    html: `<div style="font-family:Arial;padding:20px;max-width:600px"><h2 style="color:#dc2626">Oferta Especial!</h2><p>Use o cupom <strong>GURU20</strong> e ganhe 20% de desconto em qualquer plano.</p><a href="https://aipalpiteguru.mentepoderosa2030.com/planos" style="background:#dc2626;color:#fff;padding:12px 24px;border-radius:6px;text-decoration:none;display:inline-block">Ver Planos</a></div>`,
  },
  urgency: {
    subject: "⏰ Última chance — não perca!",
    html: `<div style="font-family:Arial;padding:20px;max-width:600px"><h2 style="color:#ea580c">Tempo está acabando!</h2><p>Sua oportunidade de aproveitar nossos palpites IA está se esgotando. Garanta seu acesso agora.</p><a href="https://aipalpiteguru.mentepoderosa2030.com/planos" style="background:#ea580c;color:#fff;padding:12px 24px;border-radius:6px;text-decoration:none;display:inline-block">Garantir Acesso</a></div>`,
  },
};

const SendManualEmailModal: React.FC<Props> = ({ open, onClose, onSent }) => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [templateKey, setTemplateKey] = useState("custom");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    supabase
      .from("profiles")
      .select("id, email, first_name, plan, is_subscribed, email_confirmed")
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        setProfiles((data || []) as Profile[]);
        setLoading(false);
      });
  }, [open]);

  useEffect(() => {
    const t = TEMPLATES[templateKey];
    if (templateKey !== "custom" && t) {
      setSubject(t.subject);
      setHtml(t.html);
    }
  }, [templateKey]);

  const filtered = profiles.filter((p) => {
    if (search && !p.email?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === "free") return p.plan === "free" || !p.plan;
    if (filter === "premium") return p.is_subscribed === true;
    if (filter === "unconfirmed") return p.email_confirmed === false;
    if (filter === "expired")
      return !p.is_subscribed && p.plan && p.plan !== "free";
    return true;
  });

  const toggle = (id: string) => {
    const ns = new Set(selected);
    ns.has(id) ? ns.delete(id) : ns.add(id);
    setSelected(ns);
  };

  const selectAllFiltered = () => {
    setSelected(new Set(filtered.map((p) => p.id)));
  };

  const handleSend = async () => {
    if (selected.size === 0) {
      toast({ title: "Selecione ao menos um destinatário", variant: "destructive" });
      return;
    }
    if (!subject || !html) {
      toast({ title: "Preencha assunto e corpo do email", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-manual-email", {
        body: {
          user_ids: Array.from(selected),
          subject,
          html,
          email_type: templateKey === "custom" ? "manual" : templateKey,
        },
      });
      if (error) throw error;
      toast({
        title: "Emails enviados",
        description: `${data.sent} enviados, ${data.failed} falharam de ${data.total} total`,
      });
      onSent?.();
      onClose();
      setSelected(new Set());
    } catch (e) {
      toast({ title: "Erro ao enviar", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            Enviar Email Manual
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-4">
          {/* Recipients */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Users className="w-4 h-4" /> Destinatários
            </Label>
            <div className="flex gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="free">Plano Free</SelectItem>
                  <SelectItem value="premium">Premium Ativos</SelectItem>
                  <SelectItem value="expired">Vencidos</SelectItem>
                  <SelectItem value="unconfirmed">Sem Confirmar</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={selectAllFiltered}>
                Selecionar todos
              </Button>
            </div>
            <Input
              placeholder="Buscar por email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{filtered.length} usuários</span>
              <Badge variant="secondary">{selected.size} selecionados</Badge>
            </div>
            <div className="border rounded-md max-h-64 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center">
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                </div>
              ) : (
                filtered.map((p) => (
                  <label
                    key={p.id}
                    className={`flex items-center gap-2 p-2 cursor-pointer hover:bg-muted/50 border-b text-sm ${
                      selected.has(p.id) ? "bg-primary/5" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggle(p.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{p.email}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.plan || "free"} {p.is_subscribed && "• ativo"}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Email content */}
          <div className="space-y-3">
            <div>
              <Label>Template</Label>
              <Select value={templateKey} onValueChange={setTemplateKey}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Personalizado</SelectItem>
                  <SelectItem value="welcome">Boas-vindas</SelectItem>
                  <SelectItem value="offer20">Oferta 20% OFF</SelectItem>
                  <SelectItem value="urgency">Urgência</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assunto</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div>
              <Label>Corpo (HTML)</Label>
              <Textarea
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                rows={8}
                className="font-mono text-xs"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Pré-visualização</Label>
              <div
                className="border rounded p-3 max-h-40 overflow-y-auto bg-white text-black"
                dangerouslySetInnerHTML={{ __html: html || "<p style='color:#999'>Sem conteúdo</p>" }}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={sending || selected.size === 0}>
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" /> Enviar para {selected.size}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendManualEmailModal;
